import CancelIcon from '@mui/icons-material/Cancel';
import ChatIcon from '@mui/icons-material/Chat';
import DeleteIcon from '@mui/icons-material/Delete';
import DownloadIcon from '@mui/icons-material/Download';
import MenuIcon from '@mui/icons-material/Menu';
import SystemUpdateIcon from '@mui/icons-material/SystemUpdate';
import {
    Avatar,
    Box,
    Divider,
    IconButton,
    LinearProgress,
    ListItemSecondaryAction,
    ListItemText,
    Menu,
    MenuItem,
    Stack,
    Theme,
    Toolbar,
    Tooltip,
    Typography,
} from '@mui/material';
import MuiAppBar, { AppBarProps as MuiAppBarProps } from '@mui/material/AppBar';
import { styled } from '@mui/material/styles';
import useTheme from '@mui/material/styles/useTheme';
import React, { useContext, useEffect, useState } from 'react';
import { buildStyles, CircularProgressbar } from 'react-circular-progressbar';
import { useNavigate } from 'react-router-dom';
import useSubscribers from '../hooks/useSubscribers';
import { useDownloadManager } from '../store/downloadManager/useDownloadManager';
import { UserContext } from '../store/UserContext';
import { titleCase } from '../utils/common';
import { getUserName } from '../utils/user';
import ProfileDialog from './account/ProfileDialog';
import SideMenuDrawer, { DRAWER_WIDTH } from './dashboard/sideMenu/SideMenuDrawer';

const formatBytes = (bytes: number): string => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const formatSeconds = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}m ${secs}s`;
};

interface AppBarProps extends MuiAppBarProps {
    open?: boolean;
}

const AppBar = styled(MuiAppBar, {
    shouldForwardProp: prop => prop !== 'open',
})<AppBarProps>(({ theme, open }) => ({
    transition: theme.transitions.create(['margin', 'width'], {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.leavingScreen,
    }),
    ...(open && {
        width: `calc(100% - ${DRAWER_WIDTH}px)`,
        marginLeft: `${DRAWER_WIDTH}px`,
        transition: theme.transitions.create(['margin', 'width'], {
            easing: theme.transitions.easing.easeOut,
            duration: theme.transitions.duration.enteringScreen,
        }),
    }),
}));

const Header = (props: any) => {
    const theme: Theme = useTheme();
    const userContext = useContext(UserContext);
    const navigate = useNavigate();
    const [drawerState, setDrawerState] = useState<boolean>(false);
    const [anchorElUser, setAnchorElUser] = useState<null | HTMLElement>(null);
    const [open, setOpen] = useState<any>({});
    const [profileDialogOpen, setProfileDialogOpen] = useState<boolean>(false);
    const { data: subscribers, isFetching: subscribersLoading } = useSubscribers();

    // Download manager state
    const [anchorElDownload, setAnchorElDownload] = useState<null | HTMLElement>(null);
    const { queue, current, isDownloading, progress, totalBytes, speed, etaSeconds, cancelCurrent, removeFromQueue, abortAll } =
        useDownloadManager();

    const handleOpenDownloadMenu = (event: React.MouseEvent<HTMLElement>) => {
        setAnchorElDownload(event.currentTarget);
    };

    const handleCloseDownloadMenu = () => {
        setAnchorElDownload(null);
    };

    useEffect(() => {
        if (!userContext.isAdmin && subscribers && Array.isArray(subscribers)) {
            const newOpen: any = {};

            subscribers.forEach((subscriber: any) => {
                newOpen[subscriber.username] = false;
            });

            setOpen(newOpen);
        }
    }, [subscribers, userContext.isAdmin]);

    const handleDrawerToggle = (event: React.KeyboardEvent | React.MouseEvent) => {
        if (event.type === 'keydown' && ((event as React.KeyboardEvent).key === 'Tab' || (event as React.KeyboardEvent).key === 'Shift')) {
            return;
        }

        setDrawerState(!drawerState);
    };

    const handleOpenUserMenu = (event: React.MouseEvent<HTMLElement>) => {
        setAnchorElUser(event.currentTarget);
    };

    const handleCloseUserMenu = () => {
        setAnchorElUser(null);
    };

    const handleToggleSubscriberMenu = (username: string) => {
        const newOpen: any = {};

        Object.keys(open).forEach((u: string) => {
            if (u === username) {
                newOpen[u] = !open[u];
            } else {
                newOpen[u] = false;
            }
        });

        setOpen(newOpen);
    };

    const handleProfileDialogToggle = () => {
        setProfileDialogOpen(!profileDialogOpen);
    };

    return (
        <Box sx={{ display: 'flex' }} key="dashboard">
            <AppBar position="fixed" open={drawerState}>
                <Toolbar>
                    <Stack direction="row" flexGrow={1} spacing={2} alignItems="center" justifyContent={'space-between'}>
                        <Stack direction="row" flexGrow={1} spacing={1} alignItems="center">
                            <IconButton color="inherit" aria-label="open drawer" onClick={handleDrawerToggle} edge="start">
                                <MenuIcon />
                            </IconButton>

                            <Typography variant="h6" color="inherit" component="div">
                                OnlyStruggles
                            </Typography>
                        </Stack>

                        <Stack direction="row" flexGrow={1} spacing={1} alignItems="center" justifyContent="flex-end">
                            <Tooltip title="Download Queue">
                                <IconButton
                                    onClick={handleOpenDownloadMenu}
                                    sx={{
                                        color: theme.palette.common.white,
                                        ':hover': { color: theme.palette.secondary.light },
                                    }}
                                >
                                    {!isDownloading && <DownloadIcon />}
                                    {isDownloading && (
                                        <span style={{ width: '22px', height: '22px' }}>
                                            <CircularProgressbar
                                                value={progress}
                                                strokeWidth={50}
                                                styles={buildStyles({
                                                    pathColor: theme.palette.secondary.main,
                                                    backgroundColor: theme.palette.primary.main,
                                                    strokeLinecap: 'butt',
                                                })}
                                            />
                                        </span>
                                    )}
                                </IconButton>
                            </Tooltip>

                            <Menu
                                anchorEl={anchorElDownload}
                                open={Boolean(anchorElDownload)}
                                onClose={handleCloseDownloadMenu}
                                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
                                transformOrigin={{ vertical: 'top', horizontal: 'center' }}
                            >
                                {queue.length === 0 && !isDownloading && (
                                    <MenuItem disabled sx={{ width: '250px', pl: 1, pr: 1 }}>
                                        No downloads in queue
                                    </MenuItem>
                                )}

                                {isDownloading && (
                                    <MenuItem sx={{ width: '250px', pl: 1, pr: 1 }}>
                                        <Stack
                                            direction="row"
                                            spacing={1}
                                            alignItems="center"
                                            justifyContent="space-between"
                                            sx={{ width: '100%' }}
                                        >
                                            <Stack direction="column" alignItems="flex-start" sx={{ flexGrow: 1 }}>
                                                <Typography variant="body2" color="textSecondary">
                                                    {`Downloading ${current?.media.id}...`}
                                                </Typography>
                                                <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
                                                    <Stack direction="row" spacing={1} alignItems="center">
                                                        <Typography variant="body2" color="textSecondary">
                                                            {`${progress}%`}
                                                        </Typography>
                                                        <LinearProgress variant="determinate" value={progress} sx={{ width: '100%' }} />
                                                        <Typography variant="body2" color="textSecondary">
                                                            {formatBytes(totalBytes)}
                                                        </Typography>
                                                    </Stack>
                                                </Box>
                                                <Typography variant="caption">
                                                    {speed ? `${formatBytes(speed)}/s` : '-'} — ETA:{' '}
                                                    {etaSeconds ? formatSeconds(etaSeconds) : '∞'}
                                                </Typography>
                                            </Stack>
                                            <div>
                                                <IconButton edge="end" onClick={cancelCurrent}>
                                                    <CancelIcon fontSize="small" />
                                                </IconButton>
                                            </div>
                                        </Stack>
                                    </MenuItem>
                                )}

                                {queue.map(item => (
                                    <MenuItem key={item.media.id} sx={{ width: '250px', pl: 1, pr: 1 }}>
                                        <ListItemText
                                            // primary={item.media.full?.url?.split('/').pop()?.split('?')[0] || `Media ${item.media.id}`}
                                            primary={`${titleCase(item.media.type)} ${item.media.id}`}
                                            secondary={`${item.username}`}
                                        />
                                        <ListItemSecondaryAction>
                                            <IconButton edge="end" onClick={() => removeFromQueue(item.media.id)}>
                                                <DeleteIcon fontSize="small" />
                                            </IconButton>
                                        </ListItemSecondaryAction>
                                    </MenuItem>
                                ))}

                                {(isDownloading || queue.length > 0) && <Divider sx={{ my: 0.5 }} />}
                                {(isDownloading || queue.length > 0) && (
                                    <MenuItem
                                        sx={{ width: '250px', pl: 1, pr: 1, color: theme.palette.error.main }}
                                        onClick={() => {
                                            abortAll();
                                            handleCloseDownloadMenu();
                                        }}
                                    >
                                        <Stack direction="row" spacing={1} alignItems="center">
                                            <CancelIcon fontSize="small" />
                                            <Typography variant="body2">Abort All</Typography>
                                        </Stack>
                                    </MenuItem>
                                )}
                            </Menu>

                            <Tooltip title="Contact Admin via Telegram">
                                <IconButton
                                    href="https://t.me/adamcrowe"
                                    target={'_blank'}
                                    sx={{
                                        color: theme.palette.common.white,
                                        ':hover': { color: theme.palette.secondary.light },
                                    }}
                                >
                                    <ChatIcon />
                                </IconButton>
                            </Tooltip>

                            <Tooltip title="View Service Reports on Telegram">
                                <IconButton
                                    href="https://t.me/CroweTeleBot"
                                    target={'_blank'}
                                    sx={{
                                        color: theme.palette.common.white,
                                        ':hover': { color: theme.palette.secondary.light },
                                    }}
                                >
                                    <SystemUpdateIcon />
                                </IconButton>
                            </Tooltip>

                            <Box>
                                <Tooltip title={getUserName(userContext.user)}>
                                    <IconButton onClick={handleOpenUserMenu} sx={{ p: 0 }}>
                                        <Avatar sx={{ color: theme.palette.common.white }}>
                                            {getUserName(userContext.user).charAt(0)}
                                        </Avatar>
                                    </IconButton>
                                </Tooltip>
                                <Menu
                                    sx={{ mt: '45px' }}
                                    id="menu-appbar"
                                    anchorEl={anchorElUser}
                                    anchorOrigin={{
                                        vertical: 'top',
                                        horizontal: 'right',
                                    }}
                                    keepMounted
                                    transformOrigin={{
                                        vertical: 'top',
                                        horizontal: 'right',
                                    }}
                                    open={Boolean(anchorElUser)}
                                    onClose={handleCloseUserMenu}
                                >
                                    <MenuItem
                                        onClick={() => {
                                            handleProfileDialogToggle();
                                            handleCloseUserMenu();
                                        }}
                                    >
                                        <Typography textAlign="center">Profile</Typography>
                                    </MenuItem>
                                    <Divider />
                                    <MenuItem
                                        onClick={async () => {
                                            await userContext.signOut();
                                            navigate('/', { replace: true });
                                        }}
                                    >
                                        <Typography textAlign="center">Logout</Typography>
                                    </MenuItem>
                                </Menu>
                            </Box>
                        </Stack>
                    </Stack>
                </Toolbar>
            </AppBar>

            <SideMenuDrawer
                userContext={userContext}
                drawerState={drawerState}
                open={open}
                handleDrawerToggle={handleDrawerToggle}
                handleToggleSubscriberMenu={handleToggleSubscriberMenu}
                subscribers={subscribers}
                subscribersLoading={subscribersLoading}
            />

            <ProfileDialog open={profileDialogOpen} handleClose={handleProfileDialogToggle} />
        </Box>
    );
};

export default Header;
